import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Chip,
  Skeleton,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Check,
  X,
  Sparkles,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  EllipsisVertical,
} from "lucide-react";
import { format } from "date-fns";
import useIsMobile from "components/hooks/useIsMobile";
import {
  AssignmentCandidate,
  SignupSubmission,
} from "protogen/signup_service_pb";
import { AssignmentRequest } from "protogen/assignments_pb";
import { normalizeLeadState } from "components/pipeline/utils";
import { MatchReason } from "components/matchmaking/GenerateMatches";
import DropdownMenu from "components/common/DropdownMenu";
import SkipCandidateDialog from "components/matchmaking/SkipCandidateDialog";
import ExtendExpirationDialog from "components/matchmaking/ExtendExpirationDialog";
import LinkRouter from "components/navigation/LinkRouter";
import DocumentViewer from "components/editor/DocumentViewer";

const mmFormDisplay = (mmForm: string | undefined) => {
  if (!mmForm) return null;
  let form = {};
  try {
    form = JSON.parse(mmForm);
  } catch (e) {
    return <></>;
  }
  return (
    <Box>
      <Typography variant="bodySmallHeavy">Matchmaking Form</Typography>
      {Object.entries(form).map(([key, value]) => (
        <Box key={key} sx={{ marginBottom: "12px" }}>
          <Typography variant="bodySmallHeavy">{key}</Typography>
          <Typography variant="bodySmall">{value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const getNotReadyReasons = (candidate: AssignmentCandidate) => {
  const reasons = [];
  if (!candidate.advisor?.newClientOpportunitiesEnabled)
    reasons.push("Not accepting clients");
  if ((candidate.advisor?.blurbContent?.length ?? 0) < 7)
    reasons.push("Incomplete bio");
  if (!candidate.advisor?.hasCompletedOnboardingSession)
    reasons.push("Onboarding session not completed");
  if (!candidate.advisor?.hasCompletedMatchmakingQuestionnaire) {
    reasons.push("Matchmaking questionnaire incomplete");
  }
  if (!candidate.advisor?.hasSetupApp) {
    reasons.push("App and notifications setup incomplete");
  }
  return reasons;
};

const getAssignmentStatusChip = (
  status: string,
  expiresAtSec?: bigint,
  declinedReason?: string,
) => {
  const isMobile = useIsMobile();
  switch (status) {
    case "active-waiting":
      return (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Box display="flex" flexDirection="column" justifyContent="end">
            <Box sx={{ alignSelf: isMobile ? "flex-start" : "flex-end" }}>
              <Chip size="small" color="warning" label="Waiting" />
            </Box>
            {expiresAtSec && (
              <Typography
                variant="bodySmall"
                color="textSecondary"
                sx={{ textWrap: "nowrap" }}
              >
                Expires{" "}
                {format(new Date(Number(expiresAtSec) * 1000), "h:mmaaa")}
              </Typography>
            )}
          </Box>
        </Box>
      );
    case "queued":
      return <Chip size="small" color="info" label="Queued" />;
    case "declined":
      return (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Box display="flex" flexDirection="column" justifyContent="end">
            <Box sx={{ alignSelf: isMobile ? "flex-start" : "flex-end" }}>
              <Chip size="small" color="error" label="Declined" />
            </Box>
            {declinedReason && (
              <Typography
                variant="bodySmall"
                color="textSecondary"
                sx={{ textWrap: "nowrap" }}
              >
                {declinedReason}
              </Typography>
            )}
          </Box>
        </Box>
      );
    case "timed-out":
      return <Chip size="small" color="error" label="Timed Out" />;
    case "accepted":
      return <Chip size="small" color="success" label="Accepted" />;
    default:
      return <Chip size="small" label={status} />;
  }
};

const formatSuperPowers = (advisorSP: string[], leadSP: string[]): string[] => {
  const SPNormalizer = (sp: string[], separator: string) => {
    // meal_maestro -> Meal Maestro
    return sp.map((p: string) =>
      p
        .split(separator)
        .map((parts: string) => parts.charAt(0).toUpperCase() + parts.slice(1))
        .join(" "),
    );
  };
  const normalizedLeadSP = SPNormalizer(leadSP, "-");
  const normalizedAdvisorSP = SPNormalizer(advisorSP, "_");
  const shared = normalizedAdvisorSP.filter((p: string) =>
    normalizedLeadSP.includes(p),
  );
  const advisorOnly = normalizedAdvisorSP.filter(
    (p: string) => !normalizedLeadSP.includes(p),
  );

  return [shared.join(", "), advisorOnly.join(", ")];
};

const Location = ({ candidate }: { candidate: AssignmentCandidate }) => {
  if (!candidate.advisor?.city && !candidate.advisor?.state) return <></>;
  return (
    <Typography variant="body">
      {candidate.advisor?.city}, {candidate.advisor?.state}{" "}
    </Typography>
  );
};

const DistanceAway = ({ candidate }: { candidate: AssignmentCandidate }) => {
  return (
    <Typography variant="bodySmall" color="text.secondary">
      {candidate.distanceMiles === -1
        ? "Unknown"
        : `${candidate.distanceMiles.toFixed(1)} miles`}
    </Typography>
  );
};

const NumOfClients = ({ candidate }: { candidate: AssignmentCandidate }) => {
  return (
    <Typography variant="bodySmall" color="text.secondary">
      {candidate.advisor?.families.length} active clients
    </Typography>
  );
};

const LeftAction = ({
  actionType,
  advisors,
  index,
  moveUp,
  moveDown,
  candidate,
}: {
  actionType: "existing" | "new" | "available";
  advisors?: AssignmentCandidate[];
  index?: number;
  moveUp?: (index: number) => void;
  moveDown?: (index: number) => void;
  candidate: AssignmentCandidate;
}) => {
  switch (actionType) {
    case "existing":
      return <></>;
    case "new":
      if (!advisors || index === undefined || !moveUp || !moveDown)
        return <></>;
      if (advisors.length <= 1) return <></>;
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "4px",
          }}
        >
          <IconButton
            onClick={() => moveUp(index)}
            disabled={index === 0}
            size="small"
            sx={{
              "&:disabled": {
                color: (theme) => theme.palette.gray[5],
              },
            }}
          >
            <ArrowUp />
          </IconButton>
          <IconButton
            onClick={() => moveDown(index)}
            disabled={index === advisors.length - 1}
            size="small"
            sx={{
              "&:disabled": {
                color: (theme) => theme.palette.gray[5],
              },
            }}
          >
            <ArrowDown />
          </IconButton>
        </Box>
      );
    case "available":
      const unReadyReasons = getNotReadyReasons(candidate);
      const isNew = candidate.advisor?.families.length === 0;
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isNew && unReadyReasons.length === 0 && (
            <Typography variant="bodySmallHeavy" color="primary">
              New
            </Typography>
          )}
          {unReadyReasons.length === 0 ? (
            <Box
              sx={{
                height: "32px",
                width: "32px",
                backgroundColor: "#ECF4EC",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Check stroke="#468E3E" size={21} />
            </Box>
          ) : (
            <Box
              sx={{
                height: "32px",
                width: "32px",
                backgroundColor: "#FCDBCD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X stroke="#C14743" size={21} />
            </Box>
          )}
        </Box>
      );
  }
};

const AdvisorInfo = ({ candidate }: { candidate: AssignmentCandidate }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        width: "100%",
      }}
    >
      <LinkRouter
        to={`/tools/advisor-administration/${encodeURIComponent(
          candidate.advisor?.ref ?? "",
        )}`}
      >
        <Typography variant="h4">
          {candidate.advisor?.firstName} {candidate.advisor?.lastName}
        </Typography>
      </LinkRouter>
      <Location candidate={candidate} />
      <Box sx={{ display: "flex", gap: "4px" }}>
        <DistanceAway candidate={candidate} />
        <Typography color="text.secondary"> • </Typography>
        <NumOfClients candidate={candidate} />
      </Box>
    </Box>
  );
};

const AdvisorSubInfo = ({
  candidate,
  lead,
  generateMatchesRunning,
  matchReason,
}: {
  candidate: AssignmentCandidate;
  lead?: SignupSubmission;
  generateMatchesRunning?: boolean;
  matchReason?: MatchReason;
}) => {
  const isMobile = useIsMobile();
  const normalized = lead ? normalizeLeadState(lead) : { superpowers: [] };
  const unReadyReasons = getNotReadyReasons(candidate);
  let superpowers = [];
  try {
    superpowers = JSON.parse(candidate?.superPowers || "[]");
  } catch (e) {
    // pass
  }
  const formattedSP = formatSuperPowers(superpowers, normalized.superpowers);
  const sharedSP = formattedSP[0];
  const advisorSP = formattedSP[1];
  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "1px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        flexGrow: 1,
      }}
    >
      {(matchReason || generateMatchesRunning) && (
        <Box sx={{ display: "flex", gap: "4px", alignItems: "start" }}>
          <Lightbulb size={20} stroke="#8E9598" />
          <Typography
            variant="bodySmall"
            sx={{
              width: "100%",
            }}
          >
            {generateMatchesRunning ? (
              <Skeleton
                variant="text"
                // {/* Randomly generate a width between 30% and 90% */}
                width={`${30 + 60 * Math.random()}%`}
                height="20px"
              />
            ) : (
              matchReason?.reason
            )}
          </Typography>
        </Box>
      )}
      {superpowers.length > 0 && (
        <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <Sparkles size={20} stroke="#8E9598" />
          <Typography
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              textWrap: "nowrap",
              width: "100%",
            }}
          >
            {sharedSP.length > 0 && (
              <Typography sx={{ display: "inline" }} variant="bodySmallHeavy">
                {sharedSP}
                {advisorSP.length > 0 && ","}{" "}
              </Typography>
            )}
            {advisorSP.length > 0 && (
              <Typography
                sx={{ display: "inline", color: "text.secondary" }}
                variant="bodySmall"
              >
                {advisorSP}
              </Typography>
            )}
          </Typography>
        </Box>
      )}
      {unReadyReasons.length > 0 && (
        <Typography variant="bodySmallHeavy" color="error.main">
          {unReadyReasons.join(" / ")}
        </Typography>
      )}
    </Box>
  );
};

const MoreInfoToggle = ({
  isOpen,
  onCollapse,
}: {
  isOpen: boolean;
  onCollapse: () => void;
}) => {
  return (
    <IconButton
      aria-label="expand row"
      size="small"
      onClick={() => onCollapse()}
    >
      {isOpen ? <ChevronUp /> : <ChevronDown />}
    </IconButton>
  );
};

const SubMenu = ({
  assignment,
  refresh,
}: {
  assignment: AssignmentRequest;
  refresh?: () => void;
}) => {
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [extendExpirationDialogOpen, setExtendExpirationDialogOpen] =
    useState(false);
  const onExtendTime = () => {
    setExtendExpirationDialogOpen(true);
  };
  const onSkip = () => {
    setSkipDialogOpen(true);
  };
  return (
    <>
      <DropdownMenu
        disabled={false}
        fullWidth
        buttonContent={<EllipsisVertical size={20} color="#8E9598" />}
        dropdownIcon={false}
        variant="text"
        options={[
          {
            title: "Extend time",
            onClick: () => onExtendTime(),
          },
          {
            title: <Typography color="error">Skip</Typography>,
            onClick: () => onSkip(),
          },
        ]}
        sx={{
          minWidth: "auto",
        }}
      />
      <SkipCandidateDialog
        assignment={assignment}
        isOpen={skipDialogOpen}
        onClose={() => {
          setSkipDialogOpen(false);
          refresh?.();
        }}
      />
      <ExtendExpirationDialog
        assignment={assignment}
        isOpen={extendExpirationDialogOpen}
        onClose={() => {
          setExtendExpirationDialogOpen(false);
          refresh?.();
        }}
      />
    </>
  );
};

const RightAction = ({
  actionType,
  assignment,
  candidate,
  handleSelect,
  handleRemove,
  isOpen,
  onCollapse,
  refresh,
  showAddButton,
}: {
  actionType: "existing" | "new" | "available";
  assignment?: AssignmentRequest;
  candidate: AssignmentCandidate;
  handleSelect?: (candidate: AssignmentCandidate) => void;
  handleRemove?: (candidate: AssignmentCandidate) => void;
  isOpen?: boolean;
  onCollapse?: () => void;
  refresh?: () => void;
  showAddButton?: boolean;
}) => {
  switch (actionType) {
    case "existing":
      if (!assignment) return null;
      return (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {getAssignmentStatusChip(
            assignment.status,
            assignment.expiresAtSec,
            assignment.declinedReason,
          )}
          {assignment.status === "active-waiting" && (
            <SubMenu assignment={assignment} refresh={refresh} />
          )}
          <MoreInfoToggle
            isOpen={!!isOpen}
            onCollapse={() => onCollapse && onCollapse()}
          />
        </Box>
      );
    case "new":
      return (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <IconButton onClick={() => handleRemove && handleRemove(candidate)}>
            <X />
          </IconButton>
          <MoreInfoToggle
            isOpen={!!isOpen}
            onCollapse={() => onCollapse && onCollapse()}
          />
        </Box>
      );
    case "available":
      return (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {showAddButton && (
            <Button
              disabled={!candidate.advisor?.newClientOpportunitiesEnabled}
              onClick={() => handleSelect && handleSelect(candidate)}
              variant="outlined"
            >
              Add
            </Button>
          )}
          <MoreInfoToggle
            isOpen={!!isOpen}
            onCollapse={() => onCollapse && onCollapse()}
          />
        </Box>
      );
  }
};

type Props = {
  candidate: AssignmentCandidate;
  assignment?: AssignmentRequest;
  actionType: "existing" | "new" | "available";
  advisors?: AssignmentCandidate[];
  index?: number;
  moveUp?: (index: number) => void;
  moveDown?: (index: number) => void;
  handleRemove?: (candidate: AssignmentCandidate) => void;
  handleSelect?: (candidate: AssignmentCandidate) => void;
  isLast?: boolean;
  lead?: SignupSubmission;
  generateMatchesRunning?: boolean;
  matchReason?: MatchReason;
  refresh?: () => void;
};

export default ({
  candidate,
  assignment,
  actionType,
  advisors,
  index,
  moveUp,
  moveDown,
  handleRemove,
  handleSelect,
  isLast,
  lead,
  generateMatchesRunning,
  matchReason,
  refresh,
}: Props) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        width: "100%",
        flexDirection: "column",
        display: "flex",
        gap: "24px",
        padding: "24px",
        borderBottom: "1px solid #D4D4D4",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "8px",
          width: "100%",
          ...(isLast && { borderBottom: "none" }),
        }}
      >
        <Box
          sx={{ display: "flex", gap: "20px", width: "300px", flexShrink: 0 }}
        >
          <LeftAction
            actionType={actionType}
            advisors={advisors}
            index={index}
            moveUp={moveUp}
            moveDown={moveDown}
            candidate={candidate}
          />
          <AdvisorInfo candidate={candidate} />
        </Box>
        <AdvisorSubInfo
          candidate={candidate}
          lead={lead}
          generateMatchesRunning={generateMatchesRunning}
          matchReason={matchReason}
        />
        <RightAction
          showAddButton={!!lead}
          actionType={actionType}
          assignment={assignment}
          candidate={candidate}
          handleSelect={handleSelect}
          handleRemove={handleRemove}
          isOpen={open}
          onCollapse={() => setOpen(!open)}
          refresh={refresh}
        />
      </Box>
      {open && (
        <Collapse in={open}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Divider></Divider>
            {candidate.advisor?.blurbContent && (
              <Box>
                <Typography variant="bodyHeavy">Profile</Typography>
                <DocumentViewer content={candidate.advisor?.blurbContent} />
              </Box>
            )}
            {candidate?.mmForm && mmFormDisplay(candidate?.mmForm)}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
